// src/components/ShippingForm.jsx

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  FaTimes,
  FaTruck,
  FaSpinner,
  FaCheckCircle,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCity,
  FaFlag,
  FaShieldAlt,
  FaShippingFast,
  FaSave,
  FaBoxOpen,
  FaTag,
  FaTrashAlt
} from 'react-icons/fa';

import '../styles/ShippingForm.css';

const ShippingForm = ({ pack, user, onSuccess, onCancel }) => {
  const getSavedAddressKey = () => {
    const userId = user?.id || 'guest';
    return `reanimeitor_saved_shipping_${userId}`;
  };

  const getSavedAddress = () => {
    try {
      const raw = localStorage.getItem(getSavedAddressKey());

      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);

      if (!parsed || typeof parsed !== 'object') {
        return null;
      }

      return parsed;
    } catch (error) {
      return null;
    }
  };

  const savedAddress = getSavedAddress();

  const [formData, setFormData] = useState({
    full_name: savedAddress?.full_name || user?.name || '',
    email: user?.email || '',
    phone: savedAddress?.phone ? `+34 ${savedAddress.phone}` : '+34 ',
    address: savedAddress?.address || '',
    city: savedAddress?.city || '',
    postal_code: savedAddress?.postal_code || '',
    country: savedAddress?.country || 'España'
  });

  const [saveAddress, setSaveAddress] = useState(!!savedAddress);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptToken, setAttemptToken] = useState('');
  const [savedAddressNotice, setSavedAddressNotice] = useState(
    savedAddress ? 'Dirección guardada cargada automáticamente.' : ''
  );

  const attemptCreatedRef = useRef(false);
  const formStartedRef = useRef(false);

  const TRACKING_BASE = 'https://reanimeitor.com/backend/tracking';

  const getDeviceType = () => {
    const width = window.innerWidth;

    if (width <= 768) {
      return 'mobile';
    }

    if (width <= 1024) {
      return 'tablet';
    }

    return 'desktop';
  };

  const getBrowserName = () => {
    const userAgent = navigator.userAgent || '';

    if (userAgent.includes('Edg')) {
      return 'Edge';
    }

    if (userAgent.includes('Chrome')) {
      return 'Chrome';
    }

    if (userAgent.includes('Firefox')) {
      return 'Firefox';
    }

    if (userAgent.includes('Safari')) {
      return 'Safari';
    }

    return 'Unknown';
  };

  const getStoredUser = () => {
    try {
      const rawUser = localStorage.getItem('userData');

      if (!rawUser) {
        return null;
      }

      const parsedUser = JSON.parse(rawUser);

      if (!parsedUser || !parsedUser.id) {
        return null;
      }

      return parsedUser;
    } catch (error) {
      return null;
    }
  };

  const getPackId = () => {
    return pack?.id || pack?.pack_id || pack?.packId || null;
  };

  const getPackName = () => {
    return pack?.name || pack?.pack_name || pack?.title || 'Pack Reanimeitor';
  };

  const getPackPrice = () => {
    const rawPrice = pack?.price || pack?.amount_eur || pack?.amount || 0;
    const numberPrice = Number(String(rawPrice).replace(',', '.'));

    if (Number.isNaN(numberPrice)) {
      return 0;
    }

    return numberPrice;
  };

  const getOriginalPackPrice = () => {
    const rawPrice = pack?.original_price || pack?.originalPrice || pack?.price || 0;
    const numberPrice = Number(String(rawPrice).replace(',', '.'));

    if (Number.isNaN(numberPrice)) {
      return getPackPrice();
    }

    return numberPrice;
  };

  const getFinalPackPrice = () => {
    const rawPrice = pack?.final_price || pack?.finalPrice || pack?.price || 0;
    const numberPrice = Number(String(rawPrice).replace(',', '.'));

    if (Number.isNaN(numberPrice)) {
      return getPackPrice();
    }

    return numberPrice;
  };

  const getDiscountPercent = () => {
    return Number(pack?.discount_percent || pack?.discountPercent || 0);
  };

  const getDiscountAmount = () => {
    const original = getOriginalPackPrice();
    const final = getFinalPackPrice();
    const amount = Number(pack?.discount_amount || pack?.discountAmount || original - final || 0);

    if (Number.isNaN(amount)) {
      return 0;
    }

    return Math.max(amount, 0);
  };

  const hasDiscount = () => {
    return !!pack?.has_discount || getDiscountPercent() > 0 || getDiscountAmount() > 0;
  };

  const formatPrice = (value) => {
    const numberValue = Number(value || 0);

    if (Number.isNaN(numberValue)) {
      return '0.00';
    }

    return numberValue.toFixed(2);
  };

  const createCheckoutAttempt = async () => {
    if (attemptCreatedRef.current) {
      return attemptToken;
    }

    attemptCreatedRef.current = true;

    const storedUser = getStoredUser();
    const currentUser = user || storedUser;

    try {
      const response = await axios.post(
        `${TRACKING_BASE}/checkout_start.php`,
        {
          user_id: currentUser?.id || null,
          pack_id: getPackId(),
          pack_name: getPackName(),
          amount_eur: getFinalPackPrice(),
          original_amount_eur: getOriginalPackPrice(),
          discount_code: pack?.discount_code || '',
          discount_percent: getDiscountPercent(),
          discount_amount: getDiscountAmount(),
          current_url: window.location.href,
          device: getDeviceType(),
          browser: getBrowserName()
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true,
          timeout: 10000
        }
      );

      if (response.data?.success && response.data?.attempt_token) {
        setAttemptToken(response.data.attempt_token);
        sessionStorage.setItem('reanimeitor_checkout_attempt_token', response.data.attempt_token);
        return response.data.attempt_token;
      }

      return '';
    } catch (error) {
      console.warn('No se pudo crear el intento de compra:', error);
      return '';
    }
  };

  const updateCheckoutAttempt = async (customData = {}) => {
    const token =
      customData.attempt_token ||
      attemptToken ||
      sessionStorage.getItem('reanimeitor_checkout_attempt_token') ||
      '';

    if (!token) {
      return;
    }

    const storedUser = getStoredUser();
    const currentUser = user || storedUser;

    try {
      await axios.post(
        `${TRACKING_BASE}/checkout_update.php`,
        {
          attempt_token: token,
          user_id: currentUser?.id || null,
          current_url: window.location.href,
          ...customData
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true,
          timeout: 10000
        }
      );
    } catch (error) {
      console.warn('No se pudo actualizar el intento de compra:', error);
    }
  };

  useEffect(() => {
    const initAttempt = async () => {
      const token = await createCheckoutAttempt();

      if (token) {
        await updateCheckoutAttempt({
          attempt_token: token,
          step: 'shipping_form_opened',
          status: 'shipping_form'
        });
      }
    };

    initAttempt();
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case 'full_name':
        if (!value.trim()) return 'El nombre es obligatorio';
        if (value.trim().length < 2) return 'Nombre demasiado corto';
        return '';

      case 'email':
        if (!value.trim()) return 'El email es obligatorio';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Email inválido';
        return '';

      case 'phone':
        if (!value.trim()) return 'El teléfono es obligatorio';

        const phoneNumber = value
          .replace('+34 ', '')
          .replace(/\s/g, '');

        if (phoneNumber.length < 9) {
          return 'Debe contener 9 dígitos';
        }

        if (!/^[0-9]+$/.test(phoneNumber)) {
          return 'Solo se permiten números';
        }

        return '';

      case 'address':
        if (!value.trim()) return 'La dirección es obligatoria';
        if (value.trim().length < 5) return 'Dirección demasiado corta';
        return '';

      case 'city':
        if (!value.trim()) return 'La ciudad es obligatoria';
        return '';

      case 'postal_code':
        if (!value.trim()) return 'El código postal es obligatorio';

        if (!/^[0-9]{5}$/.test(value.trim())) {
          return 'Código postal inválido';
        }

        return '';

      default:
        return '';
    }
  };

  const markFormStarted = () => {
    if (formStartedRef.current) {
      return;
    }

    formStartedRef.current = true;

    updateCheckoutAttempt({
      step: 'shipping_form_started',
      status: 'shipping_form',
      customer_name: formData.full_name,
      customer_email: formData.email,
      customer_phone: formData.phone.replace('+34 ', '').replace(/\s/g, ''),
      customer_address: formData.address,
      customer_city: formData.city,
      customer_postal_code: formData.postal_code,
      customer_country: formData.country
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    markFormStarted();
    setSavedAddressNotice('');

    if (name === 'phone') {
      let newValue = value;

      if (!newValue.startsWith('+34 ')) {
        newValue = '+34 ';
      }

      const digits = newValue.replace('+34 ', '').replace(/\s/g, '');

      if (/^\d*$/.test(digits)) {
        setFormData(prev => ({
          ...prev,
          phone: newValue
        }));

        if (touched.phone) {
          setErrors(prev => ({
            ...prev,
            phone: validateField('phone', newValue)
          }));
        }
      }

      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (touched[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, value)
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    setErrors(prev => ({
      ...prev,
      [name]: validateField(name, formData[name])
    }));

    updateCheckoutAttempt({
      step: 'shipping_form_started',
      status: 'shipping_form',
      customer_name: formData.full_name,
      customer_email: formData.email,
      customer_phone: formData.phone.replace('+34 ', '').replace(/\s/g, ''),
      customer_address: formData.address,
      customer_city: formData.city,
      customer_postal_code: formData.postal_code,
      customer_country: formData.country
    });
  };

  const handleRemoveSavedAddress = () => {
    try {
      localStorage.removeItem(getSavedAddressKey());
      setSaveAddress(false);
      setSavedAddressNotice('Dirección guardada eliminada.');

      setFormData(prev => ({
        ...prev,
        phone: '+34 ',
        address: '',
        city: '',
        postal_code: '',
        country: 'España'
      }));
    } catch (error) {
      console.warn('No se pudo eliminar la dirección guardada:', error);
    }
  };

  const handleCancel = async () => {
    await updateCheckoutAttempt({
      step: 'shipping_form_opened',
      status: 'shipping_form',
      customer_name: formData.full_name,
      customer_email: formData.email,
      customer_phone: formData.phone.replace('+34 ', '').replace(/\s/g, ''),
      customer_address: formData.address,
      customer_city: formData.city,
      customer_postal_code: formData.postal_code,
      customer_country: formData.country
    });

    if (typeof onCancel === 'function') {
      onCancel();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    let valid = true;

    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);

      if (error) {
        newErrors[field] = error;
        valid = false;
      }
    });

    setErrors(newErrors);

    setTouched(
      Object.keys(formData).reduce((acc, field) => {
        acc[field] = true;
        return acc;
      }, {})
    );

    if (!valid) return;

    setIsSubmitting(true);

    try {
      const cleanPhone = formData.phone
        .replace('+34 ', '')
        .replace(/\s/g, '');

      let token =
        attemptToken ||
        sessionStorage.getItem('reanimeitor_checkout_attempt_token') ||
        '';

      if (!token) {
        token = await createCheckoutAttempt();
      }

      if (saveAddress) {
        localStorage.setItem(
          getSavedAddressKey(),
          JSON.stringify({
            full_name: formData.full_name,
            phone: cleanPhone,
            address: formData.address,
            city: formData.city,
            postal_code: formData.postal_code,
            country: formData.country
          })
        );
      }

      await updateCheckoutAttempt({
        attempt_token: token,
        step: 'shipping_form_completed',
        status: 'shipping_form',
        customer_name: formData.full_name,
        customer_email: formData.email,
        customer_phone: cleanPhone,
        customer_address: formData.address,
        customer_city: formData.city,
        customer_postal_code: formData.postal_code,
        customer_country: formData.country,
        save_address: saveAddress ? 1 : 0,
        pack_id: getPackId(),
        pack_name: getPackName(),
        original_amount_eur: getOriginalPackPrice(),
        amount_eur: getFinalPackPrice(),
        discount_code: pack?.discount_code || '',
        discount_percent: getDiscountPercent(),
        discount_amount: getDiscountAmount()
      });

      await onSuccess({
        full_name: formData.full_name,
        email: formData.email,
        phone: cleanPhone,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country,
        save_address: saveAddress,
        attempt_token: token
      });
    } catch (err) {
      console.error(err);
      alert('Error al procesar el pedido');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="shipping-modal-overlay">
      <div className="shipping-modal shipping-modal-wide">
        <button
          type="button"
          className="shipping-modal-close"
          onClick={handleCancel}
          aria-label="Cerrar"
        >
          <FaTimes />
        </button>

        <div className="shipping-modal-header shipping-modal-header-wide">
          <div className="shipping-header-icon">
            <FaTruck />
          </div>

          <div>
            <h2>DATOS DE ENVÍO</h2>
            <p>Completa la información para recibir tu pedido Reanimeitor.</p>
          </div>
        </div>

        <div className="shipping-two-columns">
          <form onSubmit={handleSubmit} className="shipping-form shipping-form-left">
            <div className="shipping-form-title-row">
              <div>
                <span className="shipping-mini-label">Entrega</span>
                <h3>Dirección de envío</h3>
              </div>

              <FaShippingFast />
            </div>

            {savedAddressNotice && (
              <div className="shipping-saved-notice">
                <FaCheckCircle />
                {savedAddressNotice}
              </div>
            )}

            <div className="form-group">
              <label>
                <FaUser />
                Nombre completo
              </label>

              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Juan Pérez"
                className={`form-input ${
                  touched.full_name && errors.full_name ? 'input-error' : ''
                }`}
              />

              {touched.full_name && errors.full_name && (
                <span className="field-error">
                  {errors.full_name}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>
                <FaEnvelope />
                Correo electrónico
              </label>

              <input
                type="email"
                name="email"
                value={formData.email}
                disabled
                className="form-input disabled"
              />
            </div>

            <div className="form-group">
              <label>
                <FaPhone />
                Teléfono
              </label>

              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="+34 611701132"
                className={`form-input ${
                  touched.phone && errors.phone ? 'input-error' : ''
                }`}
              />

              {touched.phone && errors.phone && (
                <span className="field-error">
                  {errors.phone}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>
                <FaMapMarkerAlt />
                Dirección
              </label>

              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Calle, número, piso..."
                className={`form-input ${
                  touched.address && errors.address ? 'input-error' : ''
                }`}
              />

              {touched.address && errors.address && (
                <span className="field-error">
                  {errors.address}
                </span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <FaCity />
                  Ciudad
                </label>

                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Barcelona"
                  className={`form-input ${
                    touched.city && errors.city ? 'input-error' : ''
                  }`}
                />

                {touched.city && errors.city && (
                  <span className="field-error">
                    {errors.city}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>
                  <FaMapMarkerAlt />
                  Código Postal
                </label>

                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  maxLength={5}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="08001"
                  className={`form-input ${
                    touched.postal_code && errors.postal_code ? 'input-error' : ''
                  }`}
                />

                {touched.postal_code && errors.postal_code && (
                  <span className="field-error">
                    {errors.postal_code}
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>
                <FaFlag />
                País
              </label>

              <input
                type="text"
                value="España"
                disabled
                className="form-input disabled"
              />
            </div>

            <div className="save-address-box">
              <label className="save-address-label">
                <input
                  type="checkbox"
                  checked={saveAddress}
                  onChange={(e) => {
                    setSaveAddress(e.target.checked);
                    setSavedAddressNotice('');
                  }}
                />

                <span>
                  <FaSave />
                  Guardar esta dirección para futuras compras
                </span>
              </label>

              {savedAddress && (
                <button
                  type="button"
                  className="remove-saved-address-btn"
                  onClick={handleRemoveSavedAddress}
                >
                  <FaTrashAlt />
                  Eliminar dirección guardada
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="submit-btn"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="spinning-icon" />
                  PROCESANDO...
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  CONTINUAR AL PAGO
                </>
              )}
            </button>
          </form>

          <aside className="shipping-summary-right">
            <div className="summary-card-sticky">
              <div className="summary-top">
                <span className="shipping-mini-label">Resumen</span>
                <h3>Tu pedido</h3>
              </div>

              <div className="summary-product-image-wrap">
                {pack?.image ? (
                  <img src={pack.image} alt={getPackName()} />
                ) : (
                  <div className="summary-product-placeholder">
                    <FaBoxOpen />
                  </div>
                )}
              </div>

              <div className="summary-product-info">
                <span className="shipping-pack-badge">PEDIDO</span>
                <h4>{getPackName()}</h4>

                <div className="shipping-pack-features">
                  <span>
                    <FaShippingFast />
                    Envío 24-48h
                  </span>

                  <span>
                    <FaShieldAlt />
                    Pago seguro
                  </span>
                </div>
              </div>

              <div className="summary-price-lines">
                <div className="summary-price-line">
                  <span>Precio original</span>
                  <strong>€{formatPrice(getOriginalPackPrice())}</strong>
                </div>

                {hasDiscount() && (
                  <div className="summary-price-line discount">
                    <span>
                      <FaTag />
                      Descuento {getDiscountPercent()}%
                    </span>
                    <strong>-€{formatPrice(getDiscountAmount())}</strong>
                  </div>
                )}

                <div className="summary-price-line shipping">
                  <span>Envío</span>
                  <strong>Incluido</strong>
                </div>

                <div className="summary-total-line">
                  <span>Total</span>
                  <strong>€{formatPrice(getFinalPackPrice())}</strong>
                </div>
              </div>

              {hasDiscount() && pack?.discount_code && (
                <div className="summary-discount-code">
                  <span>Código aplicado</span>
                  <strong>{pack.discount_code}</strong>
                </div>
              )}

              <div className="summary-secure-note">
                <FaShieldAlt />
                Pago seguro con Stripe. Tus datos se procesan de forma protegida.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ShippingForm;